'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Per-user scope — klíče persistovaného stavu se izolují podle přihlášeného
 * uživatele (sdílený browser: jiný uživatel nevidí cizí drafty/filtry).
 * `userId` zapisuje authStorage při loginu; bez přihlášení → 'anon'.
 */
function userScope(): string {
  try {
    return window.localStorage.getItem('userId') || 'anon';
  } catch {
    return 'anon';
  }
}

/** Čte namespaced klíč s fallbackem na legacy (ne-namespaced) hodnotu. */
function readWithLegacy(storage: Storage, namespaced: string, legacy: string): string | null {
  try {
    const v = storage.getItem(namespaced);
    if (v !== null) return v;
    const old = storage.getItem(legacy);
    if (old !== null) {
      // Migrovat — následující čtení už jdou přes namespaced klíč.
      try { storage.setItem(namespaced, old); storage.removeItem(legacy); } catch { /* ignore */ }
      return old;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * usePersistedState — useState that persists to localStorage per-page.
 *
 * Survives page reload, idle-timeout logout + re-login, and browser tab close.
 * Key is namespaced as `armorik:persist:<pageKey>:<fieldKey>`.
 *
 * fieldKey === 'tab' is TAB-SCOPED: each browser tab keeps its own sub-tab
 * selection (sessionStorage), seeded from the last globally used value
 * (localStorage). Writes go to both — sessionStorage for this tab,
 * localStorage as the seed for future tabs. So every tab does its own work
 * while a fresh tab still opens where you left off globally.
 *
 * Usage:
 *   const [activeTab, setActiveTab] = usePersistedState('users/123', 'tab', 'prehled');
 *   // → stored as armorik:persist:users/123:tab
 *
 * For form drafts (cleared on successful submit), use usePersistedDraft instead.
 */
export function usePersistedState<T>(
  pageKey: string,
  fieldKey: string,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const uid = userScope();
  const storageKey = `armorik:persist:${uid}:${pageKey}:${fieldKey}`;
  const legacyKey = `armorik:persist:${pageKey}:${fieldKey}`;
  const isTabScoped = fieldKey === 'tab';

  const [state, setState] = useState<T>(defaultValue);

  // Restore from storage after mount (avoids SSR mismatch).
  // Tab-scoped keys: this tab's sessionStorage wins; fall back to the global
  // localStorage value so a fresh tab starts where the user left off.
  useEffect(() => {
    try {
      const stored = (isTabScoped ? readWithLegacy(window.sessionStorage, storageKey, legacyKey) : null)
        ?? readWithLegacy(window.localStorage, storageKey, legacyKey);
      if (stored !== null) {
        setState(JSON.parse(stored) as T);
      }
    } catch {
      // ignore parse errors / quota exceeded
    }
  }, [storageKey, isTabScoped]);

  const setPersistedState = useCallback(
    (value: T | ((prev: T) => T)) => {
      setState((prev) => {
        const next = value instanceof Function ? value(prev) : value;
        try {
          const json = JSON.stringify(next);
          if (isTabScoped) {
            // This tab's own value + update the global seed for future tabs.
            window.sessionStorage.setItem(storageKey, json);
            window.localStorage.setItem(storageKey, json);
          } else {
            window.localStorage.setItem(storageKey, json);
          }
        } catch {
          // ignore
        }
        return next;
      });
    },
    [storageKey, isTabScoped],
  );

  return [state, setPersistedState];
}

/**
 * usePersistedDraft — form draft that persists to sessionStorage (cleared on tab close).
 *
 * Automatically cleared via clearDraft() on successful submit.
 * Uses sessionStorage (not localStorage) because drafts are temporary —
 * if the user closes the tab, the draft should NOT survive.
 *
 * Usage:
 *   const [draft, setDraft, clearDraft] = usePersistedDraft('cases/123/edit', { title: '', body: '' });
 *   // On submit success: clearDraft();
 */
export function usePersistedDraft<T>(
  draftKey: string,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  const storageKey = `armorik:draft:${userScope()}:${draftKey}`;
  const legacyKey = `armorik:draft:${draftKey}`;

  const [state, setState] = useState<T>(defaultValue);

  useEffect(() => {
    try {
      const stored = readWithLegacy(window.sessionStorage, storageKey, legacyKey);
      if (stored !== null) {
        setState(JSON.parse(stored) as T);
      }
    } catch {
      // ignore
    }
  }, [storageKey]);

  const setDraft = useCallback(
    (value: T | ((prev: T) => T)) => {
      setState((prev) => {
        const next = value instanceof Function ? value(prev) : value;
        try {
          window.sessionStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // ignore
        }
        return next;
      });
    },
    [storageKey],
  );

  const clearDraft = useCallback(() => {
    try {
      window.sessionStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
    setState(defaultValue);
  }, [storageKey, defaultValue]);

  return [state, setDraft, clearDraft];
}

/**
 * useScrollRestore — save & restore scroll position for a scrollable container.
 *
 * Saves scroll position on scroll (debounced) and restores it after data loads.
 * Key is namespaced as `armorik:scroll:<pageKey>`.
 *
 * Usage:
 *   const scrollRef = useScrollRestore('cases/list');
 *   <div ref={scrollRef} className="overflow-y-auto">...</div>
 *
 * Or for window scroll (list pages that scroll the whole page):
 *   useScrollRestore('cases/list', true); // window mode
 */
export function useScrollRestore(pageKey: string, useWindow = false) {
  const storageKey = `armorik:scroll:${userScope()}:${pageKey}`;
  const elementRef = useRef<HTMLElement | null>(null);
  const restoreAttempted = useRef(false);

  // Save scroll position (debounced via rAF)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let rafId: number | null = null;
    const target = useWindow ? window : elementRef.current;
    if (!target) return;

    const saveScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const scrollTop = useWindow
          ? window.scrollY
          : elementRef.current?.scrollTop ?? 0;
        try {
          window.sessionStorage.setItem(storageKey, String(scrollTop));
        } catch {
          // ignore
        }
      });
    };

    target.addEventListener('scroll', saveScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', saveScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [storageKey, useWindow]);

  // Restore scroll position after mount + a small delay (let content render)
  useEffect(() => {
    if (typeof window === 'undefined' || restoreAttempted.current) return;
    restoreAttempted.current = true;

    const restore = () => {
      try {
        const saved = window.sessionStorage.getItem(storageKey);
        if (saved) {
          const scrollTop = parseInt(saved, 10);
          if (useWindow) {
            window.scrollTo(0, scrollTop);
          } else if (elementRef.current) {
            elementRef.current.scrollTop = scrollTop;
          }
        }
      } catch {
        // ignore
      }
    };

    // Delay to allow content (data tables, etc.) to render
    const timer = setTimeout(restore, 300);
    return () => clearTimeout(timer);
  }, [storageKey, useWindow]);

  return useWindow ? null : elementRef;
}
